import posthog from "posthog-js"

/** Longest wait for posthog.init() before `run` is executed anyway. */
export const ANALYTICS_READY_TIMEOUT_MS = 5000
/** Re-check interval while init is still pending. */
export const ANALYTICS_POLL_MS = 100

export function analyticsLoaded(): boolean {
  return Boolean((posthog as unknown as { __loaded?: boolean }).__loaded)
}

/**
 * Defer `run` until posthog.init() has happened, or until the timeout, and
 * return a cleanup that cancels a still-pending run.
 *
 * `initPostHog()` is called from PHProvider's mount effect (app/providers.tsx),
 * and PHProvider is an ancestor of every page. React flushes passive effects
 * bottom-up, so a leaf component's mount effect runs BEFORE init on the
 * hydration commit. posthog-js `capture()` and `register()` are silent no-ops
 * while `__loaded` is false, so anything fired straight from a leaf effect on
 * a route with no Suspense boundary is dropped with no error and no request.
 *
 * The first poll is a 0ms timer, a new macrotask after the whole passive-effect
 * flush, so in practice `run` executes on the next tick. The timeout arm keeps
 * non-production and consent-blocked builds (where init never runs) working.
 */
export function runWhenAnalyticsReady(run: () => void): () => void {
  if (typeof window === "undefined") return () => {}
  if (analyticsLoaded()) {
    run()
    return () => {}
  }

  let cancelled = false
  let waited = 0
  let timer: ReturnType<typeof setTimeout>

  const tick = () => {
    if (cancelled) return
    if (analyticsLoaded() || waited >= ANALYTICS_READY_TIMEOUT_MS) {
      run()
      return
    }
    waited += ANALYTICS_POLL_MS
    timer = setTimeout(tick, ANALYTICS_POLL_MS)
  }

  timer = setTimeout(tick, 0)
  return () => {
    cancelled = true
    clearTimeout(timer)
  }
}
